import React from "react";
import { Button } from "react-bootstrap";
import { StopCircle } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import { EventStatusEnum } from "../../../types";

import {
  handleCreateCopy,
  handleEditEvent,
  handleMoveToArchive,
  handlePublishEvent,
  handleResumeTicketsSale,
  handleStopTicketsSale,
} from "./utils/handlers";

interface StatusContentProps {
  eventId: number;
}

function ModerationContent({ eventId }: StatusContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useGetUrlLocale();

  return (
    <>
      <p className="text-info fs-7 mb-3">
        {t("events.statusMessages.moderation")}
      </p>
      <Button
        variant="success"
        className="col-12 py-2 px-4 rounded-3 mb-3"
        onClick={() => handleEditEvent(eventId, locale, navigate)}
      >
        {t("events.actions.editEvent")}
      </Button>
      <Button
        variant="outline-secondary"
        className="col-12 py-2 px-4 rounded-3 text-dark fw-normal"
        onClick={() => handleMoveToArchive(eventId)}
      >
        {t("events.actions.moveToArchive")}
      </Button>
    </>
  );
}

function ArchivedContent({ eventId }: StatusContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-info fs-7 mb-3">
        {t("events.statusMessages.archived")}
      </p>
      <Button
        variant="success"
        className="col-12 py-2 px-4 rounded-3 fw-bold"
        onClick={() => handleCreateCopy(eventId)}
      >
        {t("events.actions.createCopy")}
      </Button>
    </>
  );
}

function FinishedContent({ eventId }: StatusContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-info fs-7 mb-3">
        {t("events.statusMessages.finished")}
      </p>
      <Button
        variant="outline-secondary"
        className="col-12 py-2 px-4 rounded-3 text-dark fw-normal"
        onClick={() => handleMoveToArchive(eventId)}
      >
        {t("events.actions.moveToArchive")}
      </Button>
    </>
  );
}

function ApprovedContent({ eventId }: StatusContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-info fs-7 mb-3">
        {t("events.statusMessages.approved")}
      </p>
      <Button
        variant="outline-warning-amber"
        className="col-12 py-2 px-4 rounded-3 mb-3 d-flex align-items-center justify-content-center gap-2 fw-normal"
        onClick={() => handleStopTicketsSale(eventId)}
      >
        <StopCircle size={16} />
        {t("events.actions.stopTicketSales")}
      </Button>
      <Button
        variant="outline-secondary"
        className="col-12 py-2 px-4 rounded-3 text-dark fw-normal"
        onClick={() => handleMoveToArchive(eventId)}
      >
        {t("events.actions.moveToArchive")}
      </Button>
    </>
  );
}

function DraftContent({ eventId }: StatusContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-info fs-7 mb-3">{t("events.statusMessages.draft")}</p>
      <Button
        variant="success"
        className="col-12 py-2 px-4 rounded-3 mb-3"
        onClick={() => handlePublishEvent(String(eventId))}
      >
        {t("events.actions.publishEvent")}
      </Button>
      <Button
        variant="outline-secondary"
        className="col-12 py-2 px-4 rounded-3 text-dark fw-normal"
        onClick={() => handleMoveToArchive(eventId)}
      >
        {t("events.actions.moveToArchive")}
      </Button>
    </>
  );
}

function PausedContent({ eventId }: StatusContentProps) {
  const { t } = useTranslation();

  return (
    <>
      <p className="text-info fs-7 mb-3">{t("events.statusMessages.paused")}</p>
      <Button
        variant="success"
        className="col-12 py-2 px-4 rounded-3 mb-3"
        onClick={() => handleResumeTicketsSale(eventId)}
      >
        {t("events.actions.resumeTicketSales")}
      </Button>
      <Button
        variant="outline-secondary"
        className="col-12 py-2 px-4 rounded-3 text-dark fw-normal"
        onClick={() => handleMoveToArchive(eventId)}
      >
        {t("events.actions.moveToArchive")}
      </Button>
    </>
  );
}

function NeedsChangesContent({ eventId }: StatusContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useGetUrlLocale();

  return (
    <>
      <p className="text-info fs-7 mb-3">
        {t("events.statusMessages.needsChanges")}
      </p>
      <Button
        variant="success"
        className="col-12 py-2 px-4 rounded-3 mb-3"
        onClick={() => handleEditEvent(eventId, locale, navigate)}
      >
        {t("events.actions.editEvent")}
      </Button>
      <Button
        variant="outline-secondary"
        className="col-12 py-2 px-4 rounded-3 text-dark fw-normal"
        onClick={() => handleMoveToArchive(eventId)}
      >
        {t("events.actions.moveToArchive")}
      </Button>
    </>
  );
}

export function getStatusContent(
  status: EventStatusEnum,
  eventId: number
): React.ReactNode {
  switch (status) {
    case EventStatusEnum.ModerationInProgress:
      return <ModerationContent eventId={eventId} />;
    case EventStatusEnum.Archived:
      return <ArchivedContent eventId={eventId} />;
    case EventStatusEnum.Finished:
      return <FinishedContent eventId={eventId} />;
    case EventStatusEnum.Approved:
      return <ApprovedContent eventId={eventId} />;
    case EventStatusEnum.Draft:
      return <DraftContent eventId={eventId} />;
    case EventStatusEnum.Paused:
      return <PausedContent eventId={eventId} />;
    case EventStatusEnum.NeedsChanges:
      return <NeedsChangesContent eventId={eventId} />;
    default:
      return null;
  }
}
